import { FlyError, Link, Text } from "@wayflyer/flyui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";
import { handleContactTeam } from "shared-utils";

import type { BankConnectionSchema } from "../../api-types/bankconnect_schemas";

import { BasiqClient } from "./basiq-client";

export interface UseBankConnectWithBasiqProps {
  startAuthFlowPath: string;
  userId?: string | null;
  enabled: boolean;
}

export function useBankConnectWithBasiq({ startAuthFlowPath, enabled, userId }: UseBankConnectWithBasiqProps) {
  const intl = useIntl();
  const { openErrorModal, closeErrorModal } = useErrorModal();
  const [basiqClientInitialized, setBasiqClientInitialized] = useState(false);
  const [launchingBasiq, setLaunchingBasiq] = useState(false);
  const [reconnectingConnectionId, setReconnectingConnectionId] = useState<string | null>(null);

  const basiqClient = useMemo(() => new BasiqClient({ startAuthFlowPath, userId }), [startAuthFlowPath, userId]);

  const ready = basiqClientInitialized && basiqClient.readyForConsentUI;

  const initBasiq = useCallback(() => {
    if (enabled && !basiqClientInitialized) {
      basiqClient.initForConsentUI().then(() => {
        setBasiqClientInitialized(true);
      });
    }
  }, [basiqClient, basiqClientInitialized, enabled]);

  useEffect(() => {
    if (ready && launchingBasiq) {
      if (reconnectingConnectionId) {
        basiqClient.launchReconnectionUI(reconnectingConnectionId);
      } else {
        basiqClient.launchConsentUI();
      }
    }
  }, [basiqClient, ready, launchingBasiq, reconnectingConnectionId]);

  const displayErrorModal = () => {
    openErrorModal(
      new FlyError({
        title: intl.formatMessage({
          id: "bankconnect: available-title",
          defaultMessage: "Sorry, bank connection is not available at the moment.",
        }),
        message: (
          <FormattedMessage
            id="bankconnect: not-available-message"
            defaultMessage="<p>The service is temporarily unavailable. Please try refreshing the page, and if the problem persists <a>contact our support team</a>.</p>"
            values={{
              p: (chunks) => <Text>{chunks}</Text>,
              a: (chunks) => (
                <Link
                  onClick={() => {
                    closeErrorModal();
                    handleContactTeam();
                  }}
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        ),
        error: basiqClient.consentInitError || new Error("Basiq connection not available"),
      })
    );
  };

  function startConnect() {
    if (basiqClient.consentInitError || !enabled) {
      displayErrorModal();
    } else {
      // allows us to trigger startConnect even when basiq not ready
      // when basiq becomes ready and shouldLaunchBasiq is true then
      // the flow gets launched
      setReconnectingConnectionId(null);
      initBasiq();
      setLaunchingBasiq(true);
    }
  }

  function startReconnect(bankConnection: BankConnectionSchema) {
    if (basiqClient.consentInitError || !enabled) {
      displayErrorModal();
    } else {
      // allows us to trigger startConnect even when basiq not ready
      // when basiq becomes ready and shouldLaunchBasiq is true then
      // the flow gets launched
      setReconnectingConnectionId(bankConnection.id);
      initBasiq();
      setLaunchingBasiq(true);
    }
  }

  return {
    startConnect,
    startReconnect,
    ready,
    error: basiqClient.consentInitError,
    basiqClient: basiqClient,
    launchingBasiq,
  };
}
